// Phase 72 / 83: conditional-rule evaluator. Each CustomField can
// declare a `conditionalRule` that hides the field unless the rule
// passes against the current field-values map.
//
// v2 (Phase 83) shape:
//   { logic: "all" | "any", rules: [{ fieldSlug, op, value }] }
// with ops "equals" | "not_equals" | "in" | "set".
//
// v1 legacy shape (still in the DB on older fields):
//   { fieldSlug, op: "equals" | "in", value }
// The evaluator detects the legacy shape (no `.rules` array) and wraps
// it as { logic: "all", rules: [legacy] } so both generations evaluate
// through the same code path.
//
// Value semantics (target values can be string OR string[] - checkbox
// groups store string[]):
//   "equals"     - string target: strict === match. Array target: passes
//                  when the array INCLUDES the rule value. Unset target
//                  fails.
//   "not_equals" - strict negation of "equals". NOTE: an UNSET target
//                  passes not_equals (nothing equals the value), so a
//                  field conditioned on `other != x` shows before `other`
//                  is answered.
//   "in"         - rule value is a list (a bare string is treated as a
//                  one-member list). String target: passes when the list
//                  contains it. Array target: passes when the
//                  intersection is non-empty. Unset target fails.
//   "set"        - target has any non-empty value (string.length > 0 or
//                  array.length > 0). Rule value is ignored.
//
// IMPORTANT: this is the SINGLE gate for conditional visibility. Both
// the render layer (CustomFieldsSection) and the pricing layer
// (customFieldDeltaCents) call it, which is what guarantees a hidden
// field can never charge. Don't fork the logic.

import type {
  ConditionalRule,
  ConditionalRuleV2,
  ConditionRule,
} from "../types";

export type FieldValues = Record<string, string | string[]>;

/** Normalise either rule generation to the v2 compound shape. Returns
 *  null for null/undefined/unrecognisable input (= always visible). */
export function normaliseConditionalRule(
  rule: ConditionalRule | null | undefined,
): ConditionalRuleV2 | null {
  if (!rule || typeof rule !== "object") return null;
  const maybeV2 = rule as Partial<ConditionalRuleV2>;
  if (Array.isArray(maybeV2.rules)) {
    return {
      logic: maybeV2.logic === "any" ? "any" : "all",
      rules: maybeV2.rules.filter(
        (r): r is ConditionRule =>
          !!r && typeof r === "object" && typeof r.fieldSlug === "string",
      ),
    };
  }
  // Legacy single-rule shape: wrap as an ALL of one.
  const legacy = rule as ConditionRule;
  if (typeof legacy.fieldSlug !== "string" || !legacy.fieldSlug) return null;
  return { logic: "all", rules: [legacy] };
}

/** Evaluate ONE condition row against the values map. */
function evaluateRule(rule: ConditionRule, values: FieldValues): boolean {
  const raw = values[rule.fieldSlug];
  if (rule.op === "set") {
    if (raw == null) return false;
    return Array.isArray(raw) ? raw.length > 0 : raw.length > 0;
  }
  if (rule.op === "equals" || rule.op === "not_equals") {
    const target = typeof rule.value === "string" ? rule.value : "";
    // Multi-value source (checkboxes): "equals" means the array includes
    // the target. Unset source never equals anything.
    const eq =
      raw == null
        ? false
        : Array.isArray(raw)
          ? raw.includes(target)
          : raw === target;
    return rule.op === "equals" ? eq : !eq;
  }
  if (rule.op === "in") {
    if (raw == null) return false;
    const targets = Array.isArray(rule.value)
      ? rule.value
      : rule.value != null
        ? [rule.value]
        : [];
    if (Array.isArray(raw)) return raw.some((v) => targets.includes(v));
    return targets.includes(raw);
  }
  // Unknown op - fail closed (hide the field) so a typo in the admin
  // doesn't accidentally surface a private field.
  return false;
}

/** Returns true when the field should render. Pure function so the
 *  React layer can call it inside .filter() / render guards without
 *  worrying about side effects. */
export function evaluateCondition(
  rule: ConditionalRule | null | undefined,
  values: FieldValues,
): boolean {
  const normalised = normaliseConditionalRule(rule);
  // No rule = always visible.
  if (!normalised) return true;
  // An enabled-but-empty rule list (admin mid-edit) = always visible;
  // fail open here because there's nothing to evaluate.
  if (normalised.rules.length === 0) return true;
  return normalised.logic === "any"
    ? normalised.rules.some((r) => evaluateRule(r, values))
    : normalised.rules.every((r) => evaluateRule(r, values));
}
